import React, { useEffect } from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { GetRunEventsActionEnum } from "@app/api/generated";
import { Overview } from "@app/components/overview/overview";
import { ClickableComponent, LabelComponent } from "@app/core";
import { ROUTES } from "@app/constants/routes";
import { useGoBack } from "@app/util/use-go-back";
import BackIcon from "@assets/icons/rounded/arrow-back.svg";

import { EventsFilterBar } from "@app/components/events-filter-bar/events-filter-bar";
import { EventsTable } from "./components/events-table";
import { RunDetailSidebar } from "./components/run-detail-sidebar";
import { RUN_STATUS_LABEL_KEY } from "@app/util/run-status";
import { useRunDetail } from "@app/util/use-run-detail";
import { SYNC_RUN_DETAIL_SOURCE } from "./run-detail-source";
import { EVENT_ACTION_LABEL_KEY } from "./event-actions";
import styles from "@app/styles/dashboard/run-detail-page.module.scss";

import { useProcessedDate } from "@app/util/use-processed-date";

const ProductPageSyncDetail = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  // Back to the runs overview we came from; falls back to it on a direct landing.
  const goBack = useGoBack(ROUTES.PRODUCT_PAGE_SYNC);
  const { runId } = useParams<{ runId: string }>();
  const {
    run,
    events,
    isLoading,
    notFound,
    currentPage,
    totalPages,
    eventsTotal,
    actionFilter,
    setCurrentPage,
    setActionFilter
  } = useRunDetail(Number(runId), SYNC_RUN_DETAIL_SOURCE);

  const formatProcessedDate = useProcessedDate();

  // A non-existent run (404) → back to the overview, replacing this entry in history.
  useEffect(() => {
    if (notFound) {
      navigate(ROUTES.PRODUCT_PAGE_SYNC, { replace: true });
    }
  }, [notFound, navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={clsx(styles.container, styles.topBarContainer)}>
          <div className={styles.topBarLeft}>
            <ClickableComponent
              variant="link-primary"
              title={run?.runId ? run?.runId.toString() : "..."}
              icon={BackIcon}
              iconPosition="left"
              iconSize="24px"
              onClick={goBack}
            />
            {run && (
              <LabelComponent
                type={run.status.toLowerCase()}
                label={intl.formatMessage({ id: RUN_STATUS_LABEL_KEY[run.status] })}
              />
            )}
            <span className={styles.finishedAt}>
              {run?.finishedAt ? formatProcessedDate(new Date(run.finishedAt)) : "—"}
            </span>
          </div>
        </div>
      </div>
      <div className={styles.container}>
        {notFound ? null : (
          <div className={styles.layout}>
            <Overview
              noContainer
              className={styles.tableColumn}
              title={intl.formatMessage({ id: "productPageSync.events.title" }, { count: eventsTotal })}
              actionBar={
                <EventsFilterBar
                  actionFilter={actionFilter}
                  actions={Object.values(GetRunEventsActionEnum)}
                  labelKeyByAction={EVENT_ACTION_LABEL_KEY}
                  allLabelId="productPageSync.filter.allActions"
                  onActionChange={setActionFilter}
                />
              }
              isLoading={isLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              overviewItems={events}
            >
              <EventsTable events={events} isLoading={isLoading} />
            </Overview>
            {run && <RunDetailSidebar run={run} />}
          </div>
        )}
      </div>
    </div>
  );
};

export { ProductPageSyncDetail };
